import React, { useState, useEffect, useCallback } from "react";
import {
  makeStyles,
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Tab,
  Tabs,
  Button,
} from "@material-ui/core";
import TvIcon from "@material-ui/icons/Tv";

import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import {
  fetchRollup,
  fetchAssistantStats,
  fetchAssistantInsights,
  fetchStatsConfig,
} from "../../services/aiAssistantStatsApi";
import AiNarrativeSlot from "../../components/AiNarrativeSlot";

import KpiCards from "./panels/KpiCards";
import RecommendationsPanel from "./panels/RecommendationsPanel";
import FunnelOutcome from "./panels/FunnelOutcome";
import ObjetoReclamoPanel from "./panels/ObjetoReclamoPanel";
import TagsPanel from "./panels/TagsPanel";
import BreakdownsPanel from "./panels/BreakdownsPanel";
import CanalVentaPanel from "./panels/CanalVentaPanel";
import ClientRollupPanel from "./panels/ClientRollupPanel";
import TVOverlay from "./panels/TVOverlay";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3), maxWidth: 1200, margin: "0 auto" },
  topBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1.5),
  },
  filters: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    flexWrap: "wrap",
  },
  section: { marginBottom: theme.spacing(2) },
  assTabsPaper: { marginBottom: theme.spacing(2) },
  empty: { padding: theme.spacing(4), textAlign: "center", color: "#777" },
  spinnerWrap: { padding: theme.spacing(8), textAlign: "center" },
}));

const todayStr = () => new Date().toISOString().slice(0, 10);
const daysAgoStr = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

const AIAssistantStats = () => {
  const classes = useStyles();

  // ── config / visibility ──────────────────────────────────────────────────
  const [moduleEnabled, setModuleEnabled] = useState(null); // null=loading
  const [visibility, setVisibility] = useState({});
  // flag absent → treat as visible
  const show = useCallback((k) => visibility[k] !== false, [visibility]);

  // ── PC filter state ──────────────────────────────────────────────────────
  const [from, setFrom] = useState(daysAgoStr(7));
  const [to, setTo] = useState(todayStr());
  const [sucursal, setSucursal] = useState("all");
  const [tipoEncuesta, setTipoEncuesta] = useState("all");
  // canal de venta: "all" (todos) | "P" (convencional) | "PA" (plan de ahorro).
  // Sentinela "all" para que el Select MUI v4 muestre "Todas" seleccionado (value=""
  // deja el control en blanco). El fetch mapea "all" → no envía el filtro.
  // Reusa el filtro backend `subtipo` (P/PA solo existen en venta).
  const [canalVenta, setCanalVenta] = useState("all");

  // ── PC data state ────────────────────────────────────────────────────────
  const [rollup, setRollup] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [stats, setStats] = useState(null);
  const [insights, setInsights] = useState([]);
  const [sucursalOpts, setSucursalOpts] = useState([]);
  const [loading, setLoading] = useState(false);

  // ── TV state ─────────────────────────────────────────────────────────────
  const [tvMode, setTvMode] = useState(false);
  const [tvRollup, setTvRollup] = useState(null);
  const [tvStats, setTvStats] = useState({});
  const [tvInsights, setTvInsights] = useState({});
  const [tvLoading, setTvLoading] = useState(false);

  // ── Fetch config once ────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await fetchStatsConfig();
        if (!cancelled) {
          setModuleEnabled(!!cfg.enabled);
          setVisibility(cfg.visibility ?? {});
        }
      } catch {
        if (!cancelled) setModuleEnabled(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Fetch rollup when module ON + dates change ───────────────────────────
  const loadRollup = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchRollup(from, to);
      setRollup(data);
      const pa = data?.perAssistant ?? [];
      // auto-select when exactly 1 assistant
      if (pa.length === 1) setSelectedId(pa[0].aiAssistantId);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    if (moduleEnabled === true) loadRollup();
  }, [loadRollup, moduleEnabled]);

  // ── Fetch per-assistant stats when selectedId or filters change ──────────
  const loadAssistantStats = useCallback(async () => {
    if (!selectedId) return;
    setLoading(true);
    try {
      // "all" = sentinela de display (sin filtro) → NO se manda al backend
      // (contrato intacto: ausente/vacío = todos).
      const params = { from, to };
      if (sucursal && sucursal !== "all") params.sucursal = sucursal;
      if (tipoEncuesta && tipoEncuesta !== "all") params.tipoEncuesta = tipoEncuesta;
      if (canalVenta && canalVenta !== "all") params.subtipo = canalVenta;
      const [s, ins] = await Promise.all([
        fetchAssistantStats(selectedId, params),
        fetchAssistantInsights(selectedId, from, to),
      ]);
      setStats(s ?? null);
      setInsights(ins?.insights ?? []);
      // populate sucursal options on un-filtered load
      if ((!sucursal || sucursal === "all") && (s?.breakdowns?.bySucursal?.length ?? 0) > 0) {
        setSucursalOpts(
          (s.breakdowns.bySucursal ?? [])
            .map((b) => b.sucursal)
            .filter((v) => v && v !== "sin_dato")
        );
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [selectedId, from, to, sucursal, tipoEncuesta, canalVenta]);

  useEffect(() => {
    if (moduleEnabled === true) loadAssistantStats();
  }, [loadAssistantStats, moduleEnabled]);

  // ── Enter TV mode ────────────────────────────────────────────────────────
  const enterTV = useCallback(async () => {
    setTvMode(true);
    setTvLoading(true);
    try {
      const tvFrom = daysAgoStr(7);
      const tvTo = todayStr();
      const tvR = await fetchRollup(tvFrom, tvTo);
      setTvRollup(tvR);
      const sMap = {};
      const iMap = {};
      await Promise.all(
        (tvR?.perAssistant ?? []).map(async (a) => {
          try {
            const [s, ins] = await Promise.all([
              fetchAssistantStats(a.aiAssistantId, { from: tvFrom, to: tvTo }),
              fetchAssistantInsights(a.aiAssistantId, tvFrom, tvTo),
            ]);
            sMap[a.aiAssistantId] = s ?? null;
            iMap[a.aiAssistantId] = ins?.insights ?? [];
          } catch { /* fail-safe: slides still show, data empty */ }
        })
      );
      setTvStats(sMap);
      setTvInsights(iMap);
    } catch (err) {
      toastError(err);
    } finally {
      setTvLoading(false);
    }
  }, []);

  const exitTV = useCallback(() => setTvMode(false), []);

  // ── Select assistant ─────────────────────────────────────────────────────
  const handleSelectAssistant = useCallback((id) => {
    setSelectedId(id);
    setSucursal("");
    setSucursalOpts([]);
  }, []);

  // ── Derived KPI data ─────────────────────────────────────────────────────
  const kpiRoi = stats?.roi ?? rollup?.roi;
  const kpiCounts = {
    reclamosDetectados: stats?.reclamosDetectados ?? rollup?.totals?.reclamosDetectados ?? 0,
    iaMessages: stats?.iaMessages ?? rollup?.totals?.iaMessages ?? 0,
    humanMessages: stats?.humanMessages ?? rollup?.totals?.humanMessages ?? 0,
  };

  const perAssistant = rollup?.perAssistant ?? [];
  const multiAssistant = perAssistant.length > 1;

  // ── Disabled ──────────────────────────────────────────────────────────────
  if (moduleEnabled === false) {
    return (
      <div className={classes.root}>
        <Typography variant="h5" gutterBottom>
          {i18n.t("aiAssistantStats.heroTitle")}
        </Typography>
        <Paper className={classes.empty} elevation={1}>
          {i18n.t("aiAssistantStats.disabled")}
        </Paper>
      </div>
    );
  }

  // ── Config loading ────────────────────────────────────────────────────────
  if (moduleEnabled === null) {
    return (
      <div className={classes.spinnerWrap}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {/* TV Overlay (rendered outside normal flow) */}
      <TVOverlay
        visible={tvMode}
        onExit={exitTV}
        rollup={tvRollup}
        tvStats={tvStats}
        tvInsights={tvInsights}
        show={show}
        loading={tvLoading}
      />

      {/* Title + TV button */}
      <div className={classes.topBar}>
        <Typography variant="h5">
          {i18n.t("aiAssistantStats.heroTitle")}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<TvIcon />}
          onClick={enterTV}
        >
          {i18n.t("aiAssistantStats.modeTV")}
        </Button>
      </div>

      {/* F-next: narrativa ejecutiva IA */}
      <AiNarrativeSlot context="hero" />

      {/* Filters */}
      <Paper className={classes.filters}>
        <TextField
          label={i18n.t("aiAssistantStats.filters.from")}
          type="date"
          size="small"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label={i18n.t("aiAssistantStats.filters.to")}
          type="date"
          size="small"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <FormControl size="small" style={{ minWidth: 160 }}>
          <InputLabel>{i18n.t("aiAssistantStats.filters.sucursal")}</InputLabel>
          <Select value={sucursal} onChange={(e) => setSucursal(e.target.value)}>
            <MenuItem value="all">{i18n.t("aiAssistantStats.filters.all")}</MenuItem>
            {sucursalOpts.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" style={{ minWidth: 140 }}>
          <InputLabel>{i18n.t("aiAssistantStats.filters.tipoEncuesta")}</InputLabel>
          <Select value={tipoEncuesta} onChange={(e) => setTipoEncuesta(e.target.value)}>
            <MenuItem value="all">{i18n.t("aiAssistantStats.filters.all")}</MenuItem>
            <MenuItem value="service">{i18n.t("aiAssistantStats.filters.service")}</MenuItem>
            <MenuItem value="venta">{i18n.t("aiAssistantStats.filters.venta")}</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" style={{ minWidth: 150 }}>
          <InputLabel>{i18n.t("aiAssistantStats.canalVenta.filterLabel")}</InputLabel>
          <Select value={canalVenta} onChange={(e) => setCanalVenta(e.target.value)}>
            <MenuItem value="all">{i18n.t("aiAssistantStats.canalVenta.all")}</MenuItem>
            <MenuItem value="P">{i18n.t("aiAssistantStats.canalVenta.convencional")}</MenuItem>
            <MenuItem value="PA">{i18n.t("aiAssistantStats.canalVenta.planAhorro")}</MenuItem>
          </Select>
        </FormControl>
        {loading && <CircularProgress size={22} />}
      </Paper>

      {/* Multi-assistant: rollup summary + tab selector */}
      {multiAssistant && (
        <div className={classes.section}>
          <ClientRollupPanel rollup={rollup} onSelectAssistant={handleSelectAssistant} />
          <Paper className={classes.assTabsPaper} elevation={0} variant="outlined">
            <Tabs
              value={selectedId ?? (perAssistant[0]?.aiAssistantId ?? false)}
              onChange={(_, v) => handleSelectAssistant(v)}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
            >
              {perAssistant.map((a) => (
                <Tab
                  key={a.aiAssistantId}
                  label={a.displayName ?? a.name ?? String(a.aiAssistantId)}
                  value={a.aiAssistantId}
                />
              ))}
            </Tabs>
          </Paper>
        </div>
      )}

      {/* KPI cards (4 gated by visibility flags) */}
      <div className={classes.section}>
        <KpiCards roi={kpiRoi} counts={kpiCounts} show={show} />
      </div>

      {/* Recommendations (gated) */}
      {show("recomendaciones") && (
        <div className={classes.section}>
          <RecommendationsPanel insights={insights} />
        </div>
      )}

      {/* Funnel + Outcome (gated by embudoKpis) */}
      {show("embudoKpis") && (
        <div className={classes.section}>
          <FunnelOutcome stats={stats} />
        </div>
      )}

      {/* Objeto del reclamo (gated) */}
      {show("objetoReclamo") && (
        <div className={classes.section}>
          <ObjetoReclamoPanel stats={stats} />
        </div>
      )}

      {/* Tags (gated) */}
      {show("tags") && (
        <div className={classes.section}>
          <TagsPanel stats={stats} />
        </div>
      )}

      {/* Breakdowns by segment — PC only, gated by embudoKpis */}
      {show("embudoKpis") && <BreakdownsPanel stats={stats} />}

      {/* Canal de venta (P/PA) — PC only, gated by embudoKpis, solo si hay ventas */}
      {show("embudoKpis") && <CanalVentaPanel stats={stats} />}
    </div>
  );
};

export default AIAssistantStats;
